import {
  createContext,
  useContext,
  useState,
  Dispatch,
  ReactNode,
  SetStateAction,
} from 'react';
import { useFeatureFlags } from 'typescript/context/feature_flags';
import { type WalletPaymentSource } from 'typescript/types/api/payment_sources';

interface SavedCardsContextType {
  savedCardsEnabled: boolean;
  payWithSavedCard: boolean;
  setPayWithSavedCard: Dispatch<SetStateAction<boolean>>;
  makeDefaultCard: boolean;
  setMakeDefaultCard: Dispatch<SetStateAction<boolean>>;
  saveCardToWallet: boolean;
  setSaveCardToWallet: Dispatch<SetStateAction<boolean>>;
  selectedCard: WalletPaymentSource | null;
  setSelectedCard: Dispatch<SetStateAction<WalletPaymentSource | null>>;
}

const SavedCardsContext = createContext<SavedCardsContextType | undefined>(
  undefined,
);

/*
  Custom hook to use saved cards flow utils
 */
const useSavedCardsContext = () => {
  const context = useContext(SavedCardsContext);
  if (context === undefined) {
    throw new Error(
      'useSavedCardsContext must be used within a SavedCardsProvider',
    );
  }
  return context;
};

interface SavedCardsProviderProps {
  children: ReactNode;
}

/*
  Provides a component that wraps your app with clients and utils to complete
  checkout.
  NOTE: This must be used within a FeatureFlagsContext.
 */
const SavedCardsProvider = ({ children }: SavedCardsProviderProps) => {
  const { savedCardsEnabled } = useFeatureFlags();

  const [payWithSavedCard, setPayWithSavedCard] = useState(false);
  const [makeDefaultCard, setMakeDefaultCard] = useState(false);
  const [saveCardToWallet, setSaveCardToWallet] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  return (
    <SavedCardsContext.Provider
      value={{
        savedCardsEnabled,
        payWithSavedCard,
        setPayWithSavedCard,
        makeDefaultCard,
        setMakeDefaultCard,
        saveCardToWallet,
        setSaveCardToWallet,
        selectedCard,
        setSelectedCard,
      }}
    >
      {children}
    </SavedCardsContext.Provider>
  );
};

export { SavedCardsProvider, useSavedCardsContext };
